// src/components/admin/AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ApiURL from '../../controllers/Api'; // Adjust path to match your project structure
import { toast } from '../../utils/toast';
import { formatKolkataDate } from '../../utils/dateTime';

const getAdminTabFromPath = (pathname) => {
  if (pathname === '/admin/users') return 'users';
  if (pathname === '/admin/companies') return 'companies';
  if (pathname === '/admin/jobs') return 'jobs';
  return 'resumes';
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [filteredResumes, setFilteredResumes] = useState([]);
  const [selectedUserResumes, setSelectedUserResumes] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState('');
  const [selectedResume, setSelectedResume] = useState(null);
  const [showResumeDetails, setShowResumeDetails] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyAccessResumeIds, setCompanyAccessResumeIds] = useState([]);
  const [showCompanyAccessModal, setShowCompanyAccessModal] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [jobForm, setJobForm] = useState({
    title: '',
    company_name: '',
    company_logo: '',
    location: '',
    salary_min: '',
    salary_max: '',
    job_type: 'full-time',
    experience_level: 'entry',
    description: '',
    requirements: '',
    skills: [],
    is_remote: false,
    is_featured: false,
    application_deadline: ''
  });
  const [skillInput, setSkillInput] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    minAtsScore: '',
    experienceRange: '',
    dateRange: '',
    fileName: '',
    jobTitle: '',
    skills: ''
  });
  const activeTab = getAdminTabFromPath(location.pathname);

  useEffect(() => {
    fetchDashboardData();
    fetchJobs();
    fetchAllResumes();
    fetchCompanies();
  }, []);

  useEffect(() => {
    applyUserFilters();
  }, [filters, users]);

  useEffect(() => {
    applyResumeFilters();
  }, [filters, resumes]);

  const fetchDashboardData = async () => {
    try {
      const usersResponse = await fetch(`${ApiURL}/admin/get_users.php`);
      const usersResult = await usersResponse.json();
      
      if (usersResult.success) {
        setUsers(usersResult.data);
        setFilteredUsers(usersResult.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllResumes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${ApiURL}/admin/get_all_resumes.php`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      
      if (result.success) {
        setResumes(result.data);
        setFilteredResumes(result.data);
      }
    } catch (error) {
      console.error('Error fetching resumes:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${ApiURL}/admin/get_companies.php`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      console.log('Fetched companies:', result);
      if (result.success) {
        setCompanies(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const openCompanyAccessModal = async (company) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${ApiURL}/admin/get_company_resume_access.php?company_user_id=${company.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();

      if (result.success) {
        setSelectedCompany(company);
        setCompanyAccessResumeIds(result.data?.resume_ids || []);
        setShowCompanyAccessModal(true);
      }
    } catch (error) {
      console.error('Error loading company access:', error);
    }
  };

  const toggleCompanyResumeAccess = (resumeId) => {
    setCompanyAccessResumeIds(prev =>
      prev.includes(resumeId) ? prev.filter(id => id !== resumeId) : [...prev, resumeId]
    );
  };

  const saveCompanyResumeAccess = async () => {
    if (!selectedCompany) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${ApiURL}/admin/update_company_resume_access.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          company_user_id: selectedCompany.id,
          resume_ids: companyAccessResumeIds
        })
      });
      const result = await response.json();

      if (result.success) {
        setShowSuccessMessage('Company resume access updated successfully!');
        toast.success('Company resume access updated successfully!');
        setShowCompanyAccessModal(false);
        setSelectedCompany(null);
        fetchCompanies();
        setTimeout(() => setShowSuccessMessage(''), 3000);
      } else {
        toast.error(result.message || 'Failed to update company access');
      }
    } catch (error) {
      console.error('Error saving company access:', error);
      toast.error('Failed to update company access');
    }
  };

  const fetchUserResumes = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${ApiURL}/admin/get_user_resumes.php?user_id=${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      
      if (result.success) {
        setSelectedUserResumes(result.data);
        setShowResumeModal(true);
      }
    } catch (error) {
      console.error('Error fetching user resumes:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const jobsResponse = await fetch(`${ApiURL}/admin/get_jobs.php`);
      const jobsResult = await jobsResponse.json();
      if (jobsResult.success) {
        setJobs(jobsResult.data || []);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
    }
  };

  const applyUserFilters = () => {
    if (!users.length) return;
    
    let filtered = [...users];

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(user => 
        `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(searchTerm) ||
        (user.email || '').toLowerCase().includes(searchTerm)
      );
    }

    if (filters.fileName) {
      filtered = filtered.filter(user => 
        (user.resume?.filename || '').toLowerCase().includes(filters.fileName.toLowerCase())
      );
    }

    if (filters.category) {
      filtered = filtered.filter(user => 
        user.resume?.extracted_category === filters.category
      );
    }

    if (filters.minAtsScore) {
      const minScore = parseInt(filters.minAtsScore);
      filtered = filtered.filter(user => 
        (user.resume?.ats_score || 0) >= minScore
      );
    }

    setFilteredUsers(filtered);
  };

  const applyResumeFilters = () => {
    if (!resumes.length) return;
    
    let filtered = [...resumes];

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(resume => 
        (resume.original_filename || '').toLowerCase().includes(searchTerm) ||
        (resume.extracted_name || '').toLowerCase().includes(searchTerm) ||
        (resume.extracted_email || '').toLowerCase().includes(searchTerm) ||
        (resume.extracted_job_title || '').toLowerCase().includes(searchTerm)
      );
    }

    if (filters.category) {
      filtered = filtered.filter(resume => 
        resume.extracted_category === filters.category
      );
    }

    if (filters.jobTitle) {
      filtered = filtered.filter(resume => 
        (resume.extracted_job_title || '').toLowerCase().includes(filters.jobTitle.toLowerCase())
      );
    }

    if (filters.minAtsScore) {
      const minScore = parseInt(filters.minAtsScore);
      filtered = filtered.filter(resume => 
        (resume.ats_score || 0) >= minScore
      );
    }

    if (filters.experienceRange) {
      const [min, max] = filters.experienceRange.split('-').map(Number);
      filtered = filtered.filter(resume => {
        const exp = resume.experience_years || 0;
        return max ? exp >= min && exp <= max : exp >= min;
      });
    }

    if (filters.skills) {
      const skillTerms = filters.skills.toLowerCase().split(',').map(s => s.trim());
      filtered = filtered.filter(resume => {
        const skills = resume.skills_json ? JSON.parse(resume.skills_json) : [];
        return skillTerms.every(term => 
          skills.some(skill => skill.toLowerCase().includes(term))
        );
      });
    }

    if (filters.dateRange) {
      const now = new Date();
      const filterDate = new Date();
      
      switch(filters.dateRange) {
        case 'today':
          filterDate.setDate(now.getDate() - 1);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          filterDate.setMonth(now.getMonth() - 3);
          break;
      }
      
      filtered = filtered.filter(resume => 
        new Date(resume.upload_date) >= filterDate
      );
    }

    setFilteredResumes(filtered);
  };

  const handleViewResume = (resumeId) => {
    window.open(`${ApiURL}/admin/view_resume.php?resume_id=${resumeId}`, '_blank');
  };

  const handleViewResumeDetails = (resume) => {
    setSelectedResume(resume);
    setShowResumeDetails(true);
  };

  const handleDownloadResume = async (resumeId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${ApiURL}/admin/download_resume.php?resume_id=${resumeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading resume:', error);
    }
  };

  const handleJobInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setJobForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddSkill = () => {
    if (skillInput.trim() && !jobForm.skills.includes(skillInput.trim())) {
      setJobForm(prev => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setJobForm(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const validateJobForm = () => {
    const errors = {};
    const required = ['title', 'company_name', 'location', 'description', 'job_type', 'experience_level'];
    
    required.forEach(field => {
      if (!jobForm[field]?.trim()) {
        errors[field] = `${field.replace(/_/g, ' ')} is required`;
      }
    });

    if (jobForm.salary_min && jobForm.salary_max && 
        parseInt(jobForm.salary_min) > parseInt(jobForm.salary_max)) {
      errors.salary_max = 'Max salary must be greater than min salary';
    }

    if (jobForm.application_deadline) {
      const deadline = new Date(jobForm.application_deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (deadline < today) {
        errors.application_deadline = 'Deadline must be in the future';
      }
    }

    return errors;
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    
    const errors = validateJobForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${ApiURL}/admin/create_job.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(jobForm)
      });

      const result = await response.json();

      if (result.success) {
        setShowSuccessMessage('Job posted successfully!');
        toast.success('Job posted successfully!');
        setJobForm({
          title: '',
          company_name: '',
          company_logo: '',
          location: '',
          salary_min: '',
          salary_max: '',
          job_type: 'full-time',
          experience_level: 'entry',
          description: '',
          requirements: '',
          skills: [],
          is_remote: false,
          is_featured: false,
          application_deadline: ''
        });
        setShowJobModal(false);
        fetchJobs();
        
        setTimeout(() => setShowSuccessMessage(''), 3000);
      } else {
        toast.error(result.message || 'Failed to create job');
      }
    } catch (error) {
      console.error('Error creating job:', error);
      toast.error('Failed to create job');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${ApiURL}/admin/delete_job.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ job_id: jobId })
      });

      const result = await response.json();
      if (result.success) {
        setShowSuccessMessage('Job deleted successfully!');
        toast.success('Job deleted successfully!');
        fetchJobs();
        setTimeout(() => setShowSuccessMessage(''), 3000);
      } else {
        toast.error(result.message || 'Failed to delete job');
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  };

  const handleToggleJobStatus = async (jobId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${ApiURL}/admin/toggle_job_status.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          job_id: jobId, 
          status: currentStatus === 'active' ? 'closed' : 'active' 
        })
      });

      const result = await response.json();
      if (result.success) {
        toast.success(`Job status updated to ${currentStatus === 'active' ? 'closed' : 'active'}`);
        fetchJobs();
      } else {
        toast.error(result.message || 'Failed to update job status');
      }
    } catch (error) {
      console.error('Error toggling job status:', error);
      toast.error('Failed to update job status');
    }
  };

  const getUniqueCategories = () => {
    const categories = resumes
      .map(resume => resume.extracted_category)
      .filter(cat => cat && cat !== '')
      .filter((value, index, self) => self.indexOf(value) === index);
    return categories;
  };

  const parseExtractedData = (extractedData) => {
    if (!extractedData) return null;
    if (typeof extractedData === 'string') {
      try {
        return JSON.parse(extractedData);
      } catch {
        return null;
      }
    }
    return extractedData;
  };

  const triggerDownload = (url) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportCsv = () => {
    const token = localStorage.getItem('token');
    triggerDownload(`${ApiURL}/admin/export_resumes_csv.php?token=${encodeURIComponent(token)}`);
  };

  const handleDownloadAllResumes = () => {
    const token = localStorage.getItem('token');
    triggerDownload(`${ApiURL}/admin/download_all_resumes_zip.php?token=${encodeURIComponent(token)}`);
  };

  const formatSalary = (min, max) => {
    if (!min && !max) return 'Not specified';
    if (min && !max) return `$${parseInt(min).toLocaleString()}+`;
    if (!min && max) return `Up to $${parseInt(max).toLocaleString()}`;
    return `$${parseInt(min).toLocaleString()} - $${parseInt(max).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    return formatKolkataDate(dateString);
  };

  const getSkillCount = (resume) => {
    if (resume.skills_json) {
      try {
        const skills = JSON.parse(resume.skills_json);
        return skills.length;
      } catch {
        return 0;
      }
    }
    return 0;
  };

  const getFirstSkills = (resume, count = 3) => {
    if (resume.skills_json) {
      try {
        const skills = JSON.parse(resume.skills_json);
        return skills.slice(0, count).join(', ');
      } catch {
        return '';
      }
    }
    return '';
  };

  const handleTabChange = (tab) => {
    navigate(`/admin/${tab}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg animate-slide-in">
          {showSuccessMessage}
        </div>
      )}

      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xl">⚡</span>
              <h1 className="text-lg font-semibold text-gray-900">Admin Panel</h1>
            </div>
            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded">v1.0</span>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-4 sm:px-6">
        <div className="flex gap-6 overflow-x-auto">
          <button
            onClick={() => handleTabChange('resumes')}
            className={`flex items-center gap-1.5 py-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'resumes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span>📄</span>
            All Resumes
            <span className="ml-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
              {filteredResumes.length}
            </span>
          </button>
          <button
            onClick={() => handleTabChange('users')}
            className={`flex items-center gap-1.5 py-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span>👥</span>
            Candidates
            <span className="ml-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
              {filteredUsers.length}
            </span>
          </button>
          <button
            onClick={() => handleTabChange('companies')}
            className={`flex items-center gap-1.5 py-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'companies'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span>🏢</span>
            Companies
            <span className="ml-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
              {companies.length}
            </span>
          </button>
          <button
            onClick={() => handleTabChange('jobs')}
            className={`flex items-center gap-1.5 py-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'jobs'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span>💼</span>
            Job Management
            <span className="ml-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
              {jobs.length}
            </span>
          </button>
        </div>
      </div>

      <div className="p-3 sm:p-4">
        {activeTab === 'resumes' && (
          <div className="space-y-3">
            {/* Advanced Filters - Compact */}
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2">
                <input
                  type="text"
                  placeholder="Search name/email/file..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                
                <input
                  type="text"
                  placeholder="Job title..."
                  value={filters.jobTitle}
                  onChange={(e) => setFilters({...filters, jobTitle: e.target.value})}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />

                <select
                  value={filters.category}
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
                >
                  <option value="">All Categories</option>
                  {getUniqueCategories().map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>

                <select
                  value={filters.minAtsScore}
                  onChange={(e) => setFilters({...filters, minAtsScore: e.target.value})}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
                >
                  <option value="">ATS Score</option>
                  <option value="90">90%+ (Excellent)</option>
                  <option value="80">80%+ (Good)</option>
                  <option value="70">70%+ (Average)</option>
                  <option value="60">60%+ (Basic)</option>
                </select>

                <select
                  value={filters.experienceRange}
                  onChange={(e) => setFilters({...filters, experienceRange: e.target.value})}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
                >
                  <option value="">Experience</option>
                  <option value="0-2">0-2 years</option>
                  <option value="2-5">2-5 years</option>
                  <option value="5-10">5-10 years</option>
                  <option value="10+">10+ years</option>
                </select>

                <select
                  value={filters.dateRange}
                  onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
                >
                  <option value="">Upload Date</option>
                  <option value="today">Last 24h</option>
                  <option value="week">Last Week</option>
                  <option value="month">Last Month</option>
                  <option value="quarter">Last Quarter</option>
                </select>

                <button
                  onClick={() => setFilters({
                    search: '', category: '', minAtsScore: '', 
                    experienceRange: '', dateRange: '', fileName: '',
                    jobTitle: '', skills: ''
                  })}
                  className="px-2 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Clear
                </button>
                <div className="flex-1"></div>
                <button
                  onClick={handleExportCsv}
                  className="px-3 py-1.5 text-xs bg-green-500 text-white font-medium rounded hover:bg-green-600"
                >
                  Export CSV
                </button>
                <button
                  onClick={handleDownloadAllResumes}
                  className="px-3 py-1.5 text-xs bg-blue-500 text-white font-medium rounded hover:bg-blue-600"
                >
                  Download All Resumes
                </button>
              </div>

              {/* Skills filter - optional second row if needed */}
              <div className="mt-2">
                <input
                  type="text"
                  placeholder="Filter by skills (comma separated)..."
                  value={filters.skills}
                  onChange={(e) => setFilters({...filters, skills: e.target.value})}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Compact Table View */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">ID</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">File</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">User</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Name</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Email</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Phone</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Job Title</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Category</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Tagged Company</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Exp</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">ATS</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Skills</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Uploaded</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredResumes.map(resume => {
                      const user = users.find(u => u.id === resume.user_id);
                      const skillCount = getSkillCount(resume);
                      const firstSkills = getFirstSkills(resume, 2);
                      
                      return (
                        <tr key={resume.id} className="hover:bg-gray-50">
                          <td className="px-2 py-1.5 text-gray-600">{resume.id}</td>
                          <td className="px-2 py-1.5">
                            <div className="flex items-center gap-1">
                              <span className="text-base">
                                {resume.file_type === 'pdf' ? '📕' : '📄'}
                              </span>
                              <span className="truncate max-w-[80px]" title={resume.original_filename}>
                                {resume.original_filename?.substring(0, 15)}...
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex items-center gap-1">
                              <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-medium">
                                {user?.first_name?.[0] || ''}{user?.last_name?.[0] || ''}
                              </span>
                              <span className="text-gray-600">#{resume.user_id}</span>
                            </div>
                          </td>
                          <td className="px-2 py-1.5 font-medium text-gray-900">
                            {resume.extracted_name || 'N/A'}
                          </td>
                          <td className="px-2 py-1.5 text-gray-600 truncate max-w-[100px]">
                            {resume.extracted_email || resume.email || 'N/A'}
                          </td>
                          <td className="px-2 py-1.5 text-gray-600">
                            {resume.extracted_phone || resume.phone || 'N/A'}
                          </td>
                          <td className="px-2 py-1.5 text-gray-900">
                            {resume.extracted_job_title || resume.job_title || 'N/A'}
                          </td>
                          <td className="px-2 py-1.5">
                            <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-medium rounded">
                              {resume.extracted_category || resume.category || 'N/A'}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-gray-600 text-[10px]">
                            <div>{resume.sub_company_names?.join(', ') || resume.sub_company_name || '-'}</div>
                            {resume.requirement_notes && (
                              <div className="text-gray-400">{resume.requirement_notes}</div>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-gray-600">
                            {resume.experience_years || 0}y
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex items-center gap-1">
                              <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${
                                    (resume.ats_score || 0) >= 80 ? 'bg-green-500' :
                                    (resume.ats_score || 0) >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${resume.ats_score || 0}%` }}
                                ></div>
                              </div>
                              <span className={`text-[10px] font-medium ${
                                (resume.ats_score || 0) >= 80 ? 'text-green-600' :
                                (resume.ats_score || 0) >= 60 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {resume.ats_score || 0}%
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex items-center gap-1">
                              <span className="text-gray-600" title={firstSkills}>
                                {skillCount} skills
                              </span>
                              {skillCount > 0 && (
                                <span className="text-[10px] text-gray-400">({firstSkills})</span>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-1.5 text-gray-500 text-[10px]">
                            {formatDate(resume.upload_date)}
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleViewResume(resume.id)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                title="View"
                              >
                                👁️
                              </button>
                              <button
                                onClick={() => handleViewResumeDetails(resume)}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                title="Details"
                              >
                                📋
                              </button>
                              <button
                                onClick={() => handleDownloadResume(resume.id, resume.original_filename)}
                                className="p-1 text-purple-600 hover:bg-purple-50 rounded"
                                title="Download"
                              >
                                ⬇️
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {filteredResumes.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-3xl mb-2">📄</div>
                  <p className="text-sm text-gray-500">No resumes found</p>
                  <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
                </div>
              )}
            </div>

            {/* Stats Bar */}
            <div className="bg-white rounded-lg border border-gray-200 p-2">
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span>Total: <span className="font-bold text-blue-600">{filteredResumes.length}</span> resumes</span>
                <span>Avg ATS: <span className="font-bold text-green-600">
                  {Math.round(filteredResumes.reduce((acc, r) => acc + (r.ats_score || 0), 0) / (filteredResumes.length || 1))}%
                </span></span>
                <span>Categories: <span className="font-bold text-purple-600">{getUniqueCategories().length}</span></span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-3">
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                <input
                  type="text"
                  placeholder="Search candidates..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                
                <input
                  type="text"
                  placeholder="Filter by filename..."
                  value={filters.fileName}
                  onChange={(e) => setFilters({...filters, fileName: e.target.value})}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />

                <select
                  value={filters.category}
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
                >
                  <option value="">All Categories</option>
                  {getUniqueCategories().map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>

                <select
                  value={filters.minAtsScore}
                  onChange={(e) => setFilters({...filters, minAtsScore: e.target.value})}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
                >
                  <option value="">ATS Score</option>
                  <option value="90">90%+</option>
                  <option value="80">80%+</option>
                  <option value="70">70%+</option>
                </select>

                <button
                  onClick={() => setFilters({ search: '', fileName: '', category: '', minAtsScore: '' })}
                  className="px-2 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">ID</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Candidate</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Contact</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Resumes</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Latest Category</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Latest Job Title</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Tagged Company</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Exp</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">ATS</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredUsers.map(user => {
                      const latestResume = user.resume || null;
                      
                      return (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-2 py-1.5 text-gray-600">#{user.id}</td>
                          <td className="px-2 py-1.5">
                            <div className="flex items-center gap-1">
                              <div className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-[10px] font-medium">
                                {user.first_name?.[0] || ''}{user.last_name?.[0] || ''}
                              </div>
                              <span className="font-medium text-gray-900">{user.first_name || ''} {user.last_name || ''}</span>
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="text-[10px]">
                              <div className="text-gray-900">{user.email || ''}</div>
                              <div className="text-gray-500">{user.phone || 'No phone'}</div>
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <button
                              onClick={() => fetchUserResumes(user.id)}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] rounded hover:bg-blue-100"
                            >
                              📄 {latestResume ? 1 : 0}
                            </button>
                          </td>
                          <td className="px-2 py-1.5">
                            <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-medium rounded">
                              {latestResume?.extracted_category || 'N/A'}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-gray-900 text-[10px]">
                            {latestResume?.extracted_job_title || 'N/A'}
                          </td>
                          <td className="px-2 py-1.5 text-gray-600 text-[10px]">
                            <div>{latestResume?.sub_company_names?.join(', ') || latestResume?.sub_company_name || '-'}</div>
                            {latestResume?.requirement_notes ? (
                              <div className="text-gray-400">{latestResume.requirement_notes}</div>
                            ) : null}
                          </td>
                          <td className="px-2 py-1.5 text-gray-600 text-[10px]">
                            {latestResume?.experience_years || 0}y
                          </td>
                          <td className="px-2 py-1.5">
                            {latestResume?.ats_score ? (
                              <div className="flex items-center gap-1">
                                <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full ${
                                      latestResume.ats_score >= 80 ? 'bg-green-500' :
                                      latestResume.ats_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${latestResume.ats_score}%` }}
                                  ></div>
                                </div>
                                <span className="text-[10px] font-medium text-gray-600">
                                  {latestResume.ats_score}%
                                </span>
                              </div>
                            ) : 'N/A'}
                          </td>
                          <td className="px-2 py-1.5">
                            <button
                              onClick={() => fetchUserResumes(user.id)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              title="View Resumes"
                            >
                              📋
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'companies' && (
          <div className="space-y-3">
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500">Only resumes you explicitly grant here will appear for that company on its dashboard.</p>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">ID</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Company</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Contact</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Industry</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600">Resume Count</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {companies
                      .filter(company =>
                        !companySearch ||
                        (company.company_name || '').toLowerCase().includes(companySearch.toLowerCase()) ||
                        (company.email || '').toLowerCase().includes(companySearch.toLowerCase())
                      )
                      .map(company => (
                        <tr key={company.id} className="hover:bg-gray-50">
                          <td className="px-2 py-1.5 text-gray-600">#{company.id}</td>
                          <td className="px-2 py-1.5">
                            <div className="font-medium text-gray-900">{company.company_name || 'Company'}</div>
                            <div className="text-[10px] text-gray-500">{company.location || 'No location'}</div>
                          </td>
                          <td className="px-2 py-1.5 text-[10px]">
                            <div className="text-gray-900">{company.email || 'N/A'}</div>
                            <div className="text-gray-500">{company.phone || 'No phone'}</div>
                          </td>
                          <td className="px-2 py-1.5 text-gray-700">{company.industry || 'N/A'}</td>
                          <td className="px-2 py-1.5">
                            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full font-medium">
                              {company.approved_resume_count || 0}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'jobs' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-gray-900">Job Management</h2>
              <button
                onClick={() => setShowJobModal(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700"
              >
                <span>➕</span>
                Post New Job
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {jobs.map(job => (
                <div key={job.id} className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-sm">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-start gap-2">
                      <div className="w-8 h-8 bg-blue-50 rounded flex items-center justify-center text-blue-600 font-semibold text-xs">
                        {job.company_name?.[0] || 'C'}
                      </div>
                      <div>
                        <h3 className="text-xs font-medium text-gray-900">{job.title}</h3>
                        <p className="text-[10px] text-gray-600">{job.company_name}</p>
                      </div>
                    </div>
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${
                      job.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-600'
                    }`}>
                      {job.status || 'active'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-1 mb-2 text-[10px] text-gray-600">
                    <div className="flex items-center gap-1">
                      <span>📍</span> {job.location}
                    </div>
                    <div className="flex items-center gap-1">
                      <span>💵</span> {formatSalary(job.salary_min, job.salary_max)}
                    </div>
                    <div className="flex items-center gap-1">
                      <span>⏰</span> {job.job_type?.replace(/-/g, ' ')}
                    </div>
                    <div className="flex items-center gap-1">
                      <span>📊</span> {job.experience_level}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleToggleJobStatus(job.id, job.status)}
                        className={`text-[10px] px-2 py-1 rounded ${
                          job.status === 'active'
                            ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                            : 'bg-green-50 text-green-700 hover:bg-green-100'
                        }`}
                      >
                        {job.status === 'active' ? 'Close' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteJob(job.id)}
                        className="text-[10px] px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                    <p className="text-[10px] text-gray-400">
                      {new Date(job.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}

              {jobs.length === 0 && (
                <div className="col-span-2 bg-white rounded-lg border border-gray-200 p-8 text-center">
                  <div className="text-gray-400 text-3xl mb-2">💼</div>
                  <p className="text-xs text-gray-500">No jobs posted yet</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showCompanyAccessModal && selectedCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[85vh] overflow-hidden shadow-xl">
            <div className="sticky top-0 bg-white px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Manage Resume Access</h3>
                <p className="text-[11px] text-gray-500 mt-0.5">{selectedCompany.company_name}</p>
              </div>
              <button
                onClick={() => {
                  setShowCompanyAccessModal(false);
                  setSelectedCompany(null);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
              >
                ✕
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[65vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {resumes.map(resume => (
                  <label key={resume.id} className="flex items-start gap-3 border border-gray-200 rounded-lg p-3 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={companyAccessResumeIds.includes(resume.id)}
                      onChange={() => toggleCompanyResumeAccess(resume.id)}
                      className="mt-1"
                    />
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 text-xs truncate">
                        {resume.extracted_name || `${resume.user?.first_name || ''} ${resume.user?.last_name || ''}`.trim() || 'Candidate'}
                      </div>
                      <div className="text-[11px] text-gray-600 truncate">
                        {resume.extracted_job_title || resume.extracted_category || resume.original_filename}
                      </div>
                      <div className="text-[10px] text-gray-500 truncate">
                        Resume #{resume.id} • {resume.original_filename}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-xs text-gray-500">{companyAccessResumeIds.length} resume(s) selected for this company</p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowCompanyAccessModal(false);
                    setSelectedCompany(null);
                  }}
                  className="px-3 py-1.5 text-xs text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={saveCompanyResumeAccess}
                  className="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
                >
                  Save Access
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resume Details Modal */}
      {showResumeDetails && selectedResume && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Resume Details</h3>
              <button
                onClick={() => setShowResumeDetails(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-blue-50 p-2 rounded">
                  <p className="text-[10px] text-gray-600">ATS Score</p>
                  <p className="text-sm font-bold text-blue-700">{selectedResume.ats_score || 0}%</p>
                </div>
                <div className="bg-purple-50 p-2 rounded">
                  <p className="text-[10px] text-gray-600">Category</p>
                  <p className="text-xs font-bold text-purple-700">{selectedResume.extracted_category || 'N/A'}</p>
                </div>
                <div className="bg-green-50 p-2 rounded">
                  <p className="text-[10px] text-gray-600">Experience</p>
                  <p className="text-xs font-bold text-green-700">{selectedResume.experience_years || 0} years</p>
                </div>
                <div className="bg-orange-50 p-2 rounded">
                  <p className="text-[10px] text-gray-600">Job Title</p>
                  <p className="text-xs font-bold text-orange-700">{selectedResume.extracted_job_title || 'N/A'}</p>
                </div>
              </div>

              {(selectedResume.sub_company_names?.length || selectedResume.sub_company_name || selectedResume.requirement_notes) && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-900 mb-2">Tagged Company</h4>
                  <div className="bg-indigo-50 rounded p-2 text-xs">
                    <div><span className="text-gray-600">Company:</span> {selectedResume.sub_company_names?.join(', ') || selectedResume.sub_company_name || 'N/A'}</div>
                    {selectedResume.requirement_notes && (
                      <div className="mt-1"><span className="text-gray-600">Notes:</span> {selectedResume.requirement_notes}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Personal Info */}
              <div>
                <h4 className="text-xs font-semibold text-gray-900 mb-2">Personal Information</h4>
                <div className="bg-gray-50 rounded p-2 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-gray-600">Name:</span> {selectedResume.extracted_name || 'N/A'}</div>
                    <div><span className="text-gray-600">Email:</span> {selectedResume.extracted_email || selectedResume.email || 'N/A'}</div>
                    <div><span className="text-gray-600">Phone:</span> {selectedResume.extracted_phone || selectedResume.phone || 'N/A'}</div>
                    <div><span className="text-gray-600">Location:</span> {selectedResume.location || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Skills */}
              {selectedResume.skills_json && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-900 mb-2">Skills</h4>
                  <div className="flex flex-wrap gap-1">
                    {JSON.parse(selectedResume.skills_json).map((skill, idx) => (
                      <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 text-[10px] rounded">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Education */}
              {selectedResume.education_json && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-900 mb-2">Education</h4>
                  <div className="bg-gray-50 rounded p-2">
                    {JSON.parse(selectedResume.education_json).map((edu, idx) => (
                      <div key={idx} className="text-[10px] mb-1 last:mb-0">
                        {typeof edu === 'string' ? edu : `${edu.degree || ''} - ${edu.institution || ''}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience */}
              {selectedResume.experience_json && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-900 mb-2">Experience</h4>
                  <div className="bg-gray-50 rounded p-2">
                    {JSON.parse(selectedResume.experience_json).map((exp, idx) => (
                      <div key={idx} className="text-[10px] mb-2 last:mb-0">
                        {typeof exp === 'string' ? exp : `${exp.title || ''} at ${exp.company || ''}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {selectedResume.certifications_json && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-900 mb-2">Certifications</h4>
                  <div className="bg-gray-50 rounded p-2">
                    {JSON.parse(selectedResume.certifications_json).map((cert, idx) => (
                      <div key={idx} className="text-[10px] mb-1 last:mb-0">{cert}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Links */}
              {(selectedResume.linkedin || selectedResume.github || selectedResume.portfolio) && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-900 mb-2">Links</h4>
                  <div className="bg-gray-50 rounded p-2 text-[10px]">
                    {selectedResume.linkedin && <div>LinkedIn: {selectedResume.linkedin}</div>}
                    {selectedResume.github && <div>GitHub: {selectedResume.github}</div>}
                    {selectedResume.portfolio && <div>Portfolio: {selectedResume.portfolio}</div>}
                  </div>
                </div>
              )}

              {/* Summary */}
              {selectedResume.summary && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-900 mb-2">Summary</h4>
                  <div className="bg-gray-50 rounded p-2 text-[10px]">{selectedResume.summary}</div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                <button
                  onClick={() => handleViewResume(selectedResume.id)}
                  className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700"
                >
                  View Resume
                </button>
                <button
                  onClick={() => handleDownloadResume(selectedResume.id, selectedResume.original_filename)}
                  className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700"
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resume Modal */}
      {showResumeModal && selectedUserResumes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-6xl my-8 shadow-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Resumes - {selectedUserResumes.user?.first_name} {selectedUserResumes.user?.last_name}
                </h3>
                <p className="text-[10px] text-gray-500 mt-0.5">Total {selectedUserResumes.resumes?.length || 0} resume(s)</p>
              </div>
              <button
                onClick={() => setShowResumeModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
              >
                ✕
              </button>
            </div>

            <div className="p-4">
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">File</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Job Title</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Category</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">ATS</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Experience</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Uploaded</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {selectedUserResumes.resumes?.map(resume => (
                      <tr key={resume.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-900 font-medium">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm">📄</span>
                            <span className="truncate max-w-[120px]" title={resume.original_filename}>
                              {resume.original_filename}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {resume.extracted_job_title || 'N/A'}
                        </td>
                        <td className="px-3 py-2">
                          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-medium rounded">
                            {resume.extracted_category || 'N/A'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-medium ${
                            (resume.ats_score || 0) >= 80 ? 'text-green-600' :
                            (resume.ats_score || 0) >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {resume.ats_score || 0}%
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {resume.experience_years || 0}y
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${
                            resume.is_current ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {resume.is_current ? 'Current' : 'Archived'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-500 text-[10px]">
                          {formatKolkataDate(resume.upload_date)}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => handleViewResume(resume.id)}
                            className="px-2 py-1 bg-blue-600 text-white text-[10px] font-medium rounded hover:bg-blue-700"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Job Modal */}
      {showJobModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-2xl my-8 shadow-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Post New Job</h3>
              <button
                onClick={() => setShowJobModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateJob} className="p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">Job Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={jobForm.title}
                    onChange={handleJobInputChange}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="e.g. Senior Frontend Developer"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    name="company_name"
                    value={jobForm.company_name}
                    onChange={handleJobInputChange}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Company name"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={jobForm.location}
                    onChange={handleJobInputChange}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="e.g. New York, NY"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">Job Type *</label>
                  <select
                    name="job_type"
                    value={jobForm.job_type}
                    onChange={handleJobInputChange}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
                  >
                    <option value="full-time">Full Time</option>
                    <option value="part-time">Part Time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">Experience Level *</label>
                  <select
                    name="experience_level"
                    value={jobForm.experience_level}
                    onChange={handleJobInputChange}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
                  >
                    <option value="entry">Entry Level</option>
                    <option value="junior">Junior (1-2 years)</option>
                    <option value="mid">Mid Level (3-5 years)</option>
                    <option value="senior">Senior (6-9 years)</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">Description *</label>
                  <textarea
                    name="description"
                    rows="3"
                    value={jobForm.description}
                    onChange={handleJobInputChange}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  ></textarea>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowJobModal(false)}
                  className="px-3 py-1.5 text-xs text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
                >
                  Post Job
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

